import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import styles from "../../styles/__addAddress.module.scss";
import Footer from "../../components/footer/Footer";
import { useDispatch } from "react-redux";
import { TbLocationFilled } from "react-icons/tb";
import AddressImg from "../../assets/images/addAddress.png";
import { TbCurrentLocation } from "react-icons/tb";
import { FiTrash2 } from "react-icons/fi";
import { IoAddCircleOutline } from "react-icons/io5";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { APP_PATH } from "../../api/backendPath";
import { toast } from "../../components/app-toast";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { shouldRestrictAccess } from "../../utils/verificationUtils";
import {
  getLocalizedMessage,
  getLocalizedErrorMessage,
} from "../../utils/messageHelper";

const AddAddress = () => {
  const isRestricted = shouldRestrictAccess();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    house_flat_floor_no: "",
    street: "",
    area_district: "",
    city: "",
    governorate: "",
    country: "Iraq",
  });

  const [name, setName] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [errors, setErrors] = useState({});
  const [addAddress, setAddAddress] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  // Map and autocomplete states
  const [mapCenter, setMapCenter] = useState({ lat: 33.3152, lng: 44.3661 }); // Default to Baghdad
  const [markerPosition, setMarkerPosition] = useState(null);
  const [selectedAddressText, setSelectedAddressText] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const dropdownRef = useRef(null);

  // Google Maps API key
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [mapError, setMapError] = useState(null);

  // Memoize loader options to ensure consistent configuration across all components
  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: apiKey,
      libraries: ["places", "marker"],
      id: "google-map-script",
    }),
    [apiKey],
  );

  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);

  // Handle map load errors
  useEffect(() => {
    if (loadError) {
      console.error("Google Maps API Error:", loadError);
      const errorMsg = loadError.message || loadError.toString() || "";
      if (
        errorMsg.includes("BillingNotEnabled") ||
        errorMsg.includes("billing") ||
        errorMsg.includes("Billing")
      ) {
        setMapError("BILLING_NOT_ENABLED");
      } else if (
        errorMsg.includes("RefererNotAllowed") ||
        errorMsg.includes("referer")
      ) {
        setMapError("REFERER_NOT_ALLOWED");
      } else if (
        errorMsg.includes("InvalidKey") ||
        errorMsg.includes("invalid")
      ) {
        setMapError("INVALID_KEY");
      } else {
        setMapError("LOAD_ERROR");
      }
    } else {
      setMapError(null);
    }
  }, [loadError]);

  // Also listen for window errors (billing errors sometimes appear as console errors)
  useEffect(() => {
    const handleWindowError = (event) => {
      const errorMsg = event.message || event.error?.message || "";
      if (
        errorMsg.includes("BillingNotEnabled") ||
        errorMsg.includes("billing") ||
        errorMsg.includes("Billing")
      ) {
        setMapError("BILLING_NOT_ENABLED");
      }
    };
    window.addEventListener("error", handleWindowError);
    return () => window.removeEventListener("error", handleWindowError);
  }, []);

  // Fetch addresses on component mount - only if documents are approved
  useEffect(() => {
    // Only fetch addresses if doctor is verified (not restricted)
    if (!isRestricted) {
      fetchAddresses();
    }
  }, [isRestricted]);

  // Fetch all addresses
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const axiosPrivate = await useAxiosPrivate();
      const response = await axiosPrivate.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.getDoctorAddresses}`,
      );

      if (response?.data?.status === true && response?.data?.data?.addresses) {
        setAddresses(response.data.data.addresses);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast.error(
        error?.response?.data?.message || t("addAddress.toast.fetchFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Format address for display
  const formatAddress = (address) => {
    const parts = [];
    if (address.house_flat_floor_no) parts.push(address.house_flat_floor_no);
    if (address.street) parts.push(address.street);
    if (address.area_district) parts.push(address.area_district);
    if (address.city) parts.push(address.city);
    if (address.governorate) parts.push(address.governorate);
    if (address.postal_code) parts.push(address.postal_code);
    return parts.length > 0 ? parts.join(", ") : t("addAddress.noAddressDetails");
  };

  // Handle place selection from autocomplete
  const onPlaceSelected = useCallback((place) => {
    if (!place || !place.geometry) {
      toast.error(t("addAddress.toast.invalidPlace"));
      return;
    }

    const location = place.geometry.location;
    const lat =
      typeof location.lat === "function" ? location.lat() : location.lat;
    const lng =
      typeof location.lng === "function" ? location.lng() : location.lng;

    // Update map center and marker
    setMapCenter({ lat, lng });
    setMarkerPosition({ lat, lng });
    setLatitude(lat);
    setLongitude(lng);

    // Parse address components
    const addressComponents = place.address_components || [];
    let streetNumber = "";
    let route = "";
    let locality = "";
    let administrativeAreaLevel1 = "";
    let administrativeAreaLevel2 = "";
    let postalCode = "";
    let country = "";

    addressComponents.forEach((component) => {
      const types = component.types;

      if (types.includes("street_number")) {
        streetNumber = component.long_name;
      } else if (types.includes("route")) {
        route = component.long_name;
      } else if (types.includes("locality")) {
        locality = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        administrativeAreaLevel1 = component.long_name;
      } else if (types.includes("administrative_area_level_2")) {
        administrativeAreaLevel2 = component.long_name;
      } else if (types.includes("postal_code")) {
        postalCode = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      }
    });

    // Update form fields
    setFormData((prev) => ({
      ...prev,
      house_flat_floor_no: streetNumber || prev.house_flat_floor_no,
      street: route || prev.street,
      area_district: administrativeAreaLevel2 || prev.area_district,
      city: locality || prev.city,
      governorate: administrativeAreaLevel1 || prev.governorate,
      country: country || prev.country || "Iraq",
    }));

    setPostalCode(postalCode || "");

    // Update selected address text for display
    const formattedAddr = place.formatted_address || "";
    setSelectedAddressText(formattedAddr);

    // Update the input field value
    if (searchInputRef.current) {
      searchInputRef.current.value = formattedAddr;
    }

    // Clear any errors
    setErrors({});
  }, [t]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLocationSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch coordinates from place ID
  const fetchCoordinatesFromPlaceId = async (placeId) => {
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const place = new Place({ id: placeId });
      await place.fetchFields({
        fields: ["location", "formattedAddress", "addressComponents"],
      });

      if (place.location) {
        const lat = place.location.lat();
        const lng = place.location.lng();

        // Update form with place details
        if (place.addressComponents) {
          const addressComponents = place.addressComponents;
          let streetNumber = "";
          let route = "";
          let locality = "";
          let administrativeAreaLevel1 = "";
          let administrativeAreaLevel2 = "";
          let postalCode = "";
          let country = "";

          addressComponents.forEach((component) => {
            const types = component.types;

            if (types.includes("street_number")) {
              streetNumber = component.longText || component.shortText || "";
            } else if (types.includes("route")) {
              route = component.longText || component.shortText || "";
            } else if (types.includes("locality")) {
              locality = component.longText || component.shortText || "";
            } else if (types.includes("administrative_area_level_1")) {
              administrativeAreaLevel1 =
                component.longText || component.shortText || "";
            } else if (types.includes("administrative_area_level_2")) {
              administrativeAreaLevel2 =
                component.longText || component.shortText || "";
            } else if (types.includes("postal_code")) {
              postalCode = component.longText || component.shortText || "";
            } else if (types.includes("country")) {
              country = component.longText || component.shortText || "";
            }
          });

          setFormData((prev) => ({
            ...prev,
            house_flat_floor_no: streetNumber || prev.house_flat_floor_no,
            street: route || prev.street,
            area_district: administrativeAreaLevel2 || prev.area_district,
            city: locality || prev.city,
            governorate: administrativeAreaLevel1 || prev.governorate,
            country: country || prev.country || "Iraq",
          }));

          setPostalCode(postalCode || "");
        }

        return {
          latitude: lat,
          longitude: lng,
          formattedAddress: place.formattedAddress || "",
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  // Fetch location suggestions using new Places API
  const fetchLocationSuggestions = async (input) => {
    if (!input || input.trim().length < 3) {
      setLocationSuggestions([]);
      return;
    }

    try {
      if (!window.google?.maps) {
        console.error("Google Maps not loaded");
        return;
      }

      const { AutocompleteSuggestion } =
        await window.google.maps.importLibrary("places");

      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }

      const request = {
        input: input,
        sessionToken: sessionTokenRef.current,
      };

      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const formattedSuggestions = suggestions.map((suggestion) => ({
        description: suggestion.placePrediction.text.toString(),
        placeId: suggestion.placePrediction.placeId,
      }));

      setLocationSuggestions(formattedSuggestions);
    } catch (error) {
      console.error("Autocomplete suggestions error", error);
      setLocationSuggestions([]);
    }
  };

  // Handle location input change
  const handleLocationInputChange = (value) => {
    setSelectedAddressText(value);
    fetchLocationSuggestions(value);
  };

  // Handle map load
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Handle map click
  const onMapClick = useCallback(
    (e) => {
      if (e.latLng) {
        const lat =
          typeof e.latLng.lat === "function" ? e.latLng.lat() : e.latLng.lat;
        const lng =
          typeof e.latLng.lng === "function" ? e.latLng.lng() : e.latLng.lng;
        setMarkerPosition({ lat, lng });
        setLatitude(lat);
        setLongitude(lng);

        // Reverse geocode to get address details
        if (
          window.google &&
          window.google.maps &&
          window.google.maps.Geocoder
        ) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              onPlaceSelected(results[0]);
              // Update search input with selected address
              if (searchInputRef.current) {
                searchInputRef.current.value =
                  results[0].formatted_address || "";
              }
            } else {
              toast.error(t("addAddress.toast.addressNotFoundForLocation"));
            }
          });
        }
      }
    },
    [onPlaceSelected, t],
  );

  // Handle current location button click
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("addAddress.geolocation.notSupported"));
      return;
    }

    toast.info(t("addAddress.geolocation.gettingLocation"));
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setLatitude(lat);
        setLongitude(lng);

        // Reverse geocode
        if (
          window.google &&
          window.google.maps &&
          window.google.maps.Geocoder
        ) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              onPlaceSelected(results[0]);
              // Update search input
              if (searchInputRef.current) {
                searchInputRef.current.value =
                  results[0].formatted_address || "";
              }
              if (mapRef.current) {
                mapRef.current.setCenter({ lat, lng });
                mapRef.current.setZoom(15);
              }
            } else {
              toast.error(t("addAddress.toast.addressNotFoundForYourLocation"));
            }
          });
        }
      },
      (error) => {
        let errorMsg = t("addAddress.geolocation.unableToGetLocation");
        if (error.code === error.PERMISSION_DENIED) {
          errorMsg = t("addAddress.geolocation.permissionDenied");
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMsg = t("addAddress.geolocation.positionUnavailable");
        } else if (error.code === error.TIMEOUT) {
          errorMsg = t("addAddress.geolocation.timeout");
        }
        toast.error(errorMsg);
        console.error("Geolocation error:", error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  // ✅ Handle Input Change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ✅ Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    const newErrors = {};

    // Validate required fields
    if (!formData.house_flat_floor_no?.trim()) {
      newErrors.house_flat_floor_no = t("addAddress.validation.houseFlatFloorRequired");
      valid = false;
    }

    if (!formData.street?.trim()) {
      newErrors.street = t("addAddress.validation.streetRequired");
      valid = false;
    }

    if (!name.trim()) {
      newErrors.name = t("addAddress.validation.nameRequired");
      valid = false;
    }

    setErrors(newErrors);

    if (valid) {
      await handleAddOrUpdateAddress();
    }
  };

  // ✅ Handle Add or Update Address
  const handleAddOrUpdateAddress = async () => {
    try {
      setLoading(true);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const axiosPrivate = await useAxiosPrivate();

      const payload = {
        action: editingAddressId ? "update" : "add",
        ...(editingAddressId && { address_id: editingAddressId }),
        name: name.trim(),
        house_flat_floor_no: formData.house_flat_floor_no?.trim() || null,
        street: formData.street?.trim() || null,
        area_district: formData.area_district?.trim() || null,
        city: formData.city?.trim() || null,
        governorate: formData.governorate?.trim() || null,
        postal_code: postalCode?.trim() || null,
        country: formData.country?.trim() || null,
        ...(latitude !== null && { latitude }),
        ...(longitude !== null && { longitude }),
      };

      const response = await axiosPrivate.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.manageDoctorAddress}`,
        payload,
      );

      if (response?.data?.status === true) {
        toast.success(
          editingAddressId
            ? t("addAddress.toast.updated")
            : t("addAddress.toast.added"),
        );
        // Reset form
        resetForm();
        // Refresh addresses list
        await fetchAddresses();
        // Switch back to list view
        setAddAddress(false);
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error(error?.response?.data?.message || t("addAddress.toast.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Delete Address
  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(t("addAddress.confirmDelete"))) {
      return;
    }

    try {
      setLoading(true);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const axiosPrivate = await useAxiosPrivate();

      const response = await axiosPrivate.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.manageDoctorAddress}`,
        {
          action: "delete",
          address_id: addressId,
        },
      );

      if (response?.data?.status === true) {
        toast.success(t("addAddress.toast.deleted"));
        await fetchAddresses();
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error(error?.response?.data?.message || t("addAddress.toast.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Set Default Address
  const handleSetDefaultAddress = async (addressId) => {
    try {
      setLoading(true);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const axiosPrivate = await useAxiosPrivate();

      const response = await axiosPrivate.put(
        `${process.env.REACT_APP_API_URL}${APP_PATH.setDefaultDoctorAddress}`,
        {
          address_id: addressId,
        },
      );

      if (response?.data?.status === true) {
        const successMessage = getLocalizedMessage(response, i18n);
        toast.success(successMessage);
        await fetchAddresses();
      }
    } catch (error) {
      console.error("Error setting default address:", error);
      const errorMessage = getLocalizedErrorMessage(error, i18n);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Edit Address
  const handleEditAddress = (address) => {
    setName(address.name || "");
    setFormData({
      house_flat_floor_no: address.house_flat_floor_no || "",
      street: address.street || "",
      area_district: address.area_district || "",
      city: address.city || "",
      governorate: address.governorate || "",
      country: address.country || "Iraq",
    });
    setPostalCode(address.postal_code || "");

    // Set map position if coordinates exist
    if (address.latitude && address.longitude) {
      const lat = parseFloat(address.latitude);
      const lng = parseFloat(address.longitude);
      setLatitude(lat);
      setLongitude(lng);
      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });

      // Set address text
      setSelectedAddressText(formatAddress(address));
    }

    setEditingAddressId(address.id);
    setAddAddress(true);
  };

  // ✅ Reset Form
  const resetForm = () => {
    setName("");
    setFormData({
      house_flat_floor_no: "",
      street: "",
      area_district: "",
      city: "",
      governorate: "",
      country: "Iraq",
    });
    setPostalCode("");
    setLatitude(null);
    setLongitude(null);
    setEditingAddressId(null);
    setErrors({});
    setSelectedAddressText("");
    setLocationSuggestions([]);
    setMarkerPosition(null);
    setMapCenter({ lat: 33.3152, lng: 44.3661 });
    sessionTokenRef.current = null;
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  // ✅ Handle Continue without changes
  const handleConfirmAndContinue = async () => {
    await handleAddOrUpdateAddress();
  };

  // ✅ Handle Cancel/Back
  const handleCancel = () => {
    resetForm();
    setAddAddress(false);
  };

  const AddressCard = ({
    address,
    isDefault = false,
    onSetDefault,
    onDelete,
    onEdit,
  }) => {
    return (
      <div className={styles.card}>
        <div className={styles.left}>
          {isDefault ? (
            <span className={styles.defaultBadge}>{t("registration.default")}</span>
          ) : (
            <button className={styles.setDefaultBtn} onClick={onSetDefault}>
              {t("registration.setAsDefault")}
            </button>
          )}

          <h4 className={styles.name}>{address.name || t("addAddress.untitledAddress")}</h4>
          <p className={styles.address}>{formatAddress(address)}</p>
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className={styles.editBtn}
            onClick={onEdit}
            aria-label={t("addAddress.card.editAria")}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            {t("common.edit")}
          </button>
          <button
            className={styles.deleteBtn}
            onClick={onDelete}
            aria-label={t("addAddress.card.deleteAria")}
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles["main-container"]}>
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {addAddress ? (
            <>
              {/* Location Form */}
              <div className={styles["login-form-container"]}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <h2 className={styles["addressTitle"]}>
                    {editingAddressId
                      ? t("addAddress.title.edit")
                      : t("addAddress.title.add")}
                  </h2>
                  <button
                    onClick={handleCancel}
                    style={{
                      background: "transparent",
                      border: "1px solid #ccc",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>

                <div className={styles["mapWrapper"]}>
                  {!apiKey ? (
                    <>
                      <img src={AddressImg} alt={t("addAddress.map.placeholderAlt")} />
                      <div
                        style={{
                          padding: "15px",
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: "4px",
                          marginTop: "10px",
                          fontSize: "14px",
                          color: "#856404",
                        }}
                      >
                        <strong>{t("addAddress.map.apiKeyMissing.title")}</strong>
                        <p style={{ margin: "5px 0 0 0" }}>
                          {t("addAddress.map.apiKeyMissing.message")}
                        </p>
                      </div>
                    </>
                  ) : mapError === "BILLING_NOT_ENABLED" ? (
                    <>
                      <img src={AddressImg} alt={t("addAddress.map.placeholderAlt")} />
                      <div
                        style={{
                          padding: "15px",
                          backgroundColor: "#f8d7da",
                          border: "1px solid #dc3545",
                          borderRadius: "4px",
                          marginTop: "10px",
                          fontSize: "14px",
                          color: "#721c24",
                        }}
                      >
                        <strong>⚠️ {t("addAddress.map.billing.title")}</strong>
                        <p style={{ margin: "8px 0 0 0" }}>
                          {t("addAddress.map.billing.intro")}
                        </p>
                        <ol
                          style={{
                            margin: "10px 0 0 20px",
                            paddingLeft: "0",
                            lineHeight: "1.6",
                          }}
                        >
                          <li>
                            {t("addAddress.map.billing.goTo")}{" "}
                            <a
                              href="https://console.cloud.google.com/"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#0056b3",
                                textDecoration: "underline",
                              }}
                            >
                              Google Cloud Console
                            </a>
                          </li>
                          <li>{t("addAddress.map.billing.selectProject")}</li>
                          <li>
                            {t("addAddress.map.billing.goTo")} <strong>Billing</strong> →{" "}
                            {t("addAddress.map.billing.enableBilling")}
                          </li>
                          <li>
                            {t("addAddress.map.billing.goTo")} <strong>APIs & Services</strong> →{" "}
                            <strong>Library</strong>
                          </li>
                          <li>
                            {t("addAddress.map.billing.enableApis")}
                            <ul
                              style={{ marginTop: "5px", marginBottom: "5px" }}
                            >
                              <li>"Maps JavaScript API"</li>
                              <li>"Places API"</li>
                              <li>
                                "Geocoding API"{" "}
                                {t("addAddress.map.billing.geocodingOptional")}
                              </li>
                            </ul>
                          </li>
                          <li>{t("addAddress.map.billing.waitForPropagation")}</li>
                          <li>{t("addAddress.map.billing.refreshPage")}</li>
                        </ol>
                        <p
                          style={{
                            margin: "10px 0 0 0",
                            fontSize: "12px",
                            fontStyle: "italic",
                            color: "#856404",
                            backgroundColor: "#fff3cd",
                            padding: "8px",
                            borderRadius: "4px",
                          }}
                        >
                          💡 {t("addAddress.map.billing.notePrefix")}{" "}
                          <strong>{t("addAddress.map.billing.noteCredit")}</strong>
                          {t("addAddress.map.billing.noteSuffix")}
                        </p>
                        <p style={{ margin: "10px 0 0 0", fontSize: "12px" }}>
                          <strong>{t("addAddress.map.billing.alternativeLabel")}</strong>{" "}
                          {t("addAddress.map.billing.alternativeText")}
                        </p>
                      </div>
                    </>
                  ) : mapError === "INVALID_KEY" ? (
                    <>
                      <img src={AddressImg} alt={t("addAddress.map.placeholderAlt")} />
                      <div
                        style={{
                          padding: "15px",
                          backgroundColor: "#f8d7da",
                          border: "1px solid #dc3545",
                          borderRadius: "4px",
                          marginTop: "10px",
                          fontSize: "14px",
                          color: "#721c24",
                        }}
                      >
                        <strong>❌ {t("addAddress.map.invalidKey.title")}</strong>
                        <p style={{ margin: "5px 0 0 0" }}>
                          {t("addAddress.map.invalidKey.message")}
                        </p>
                      </div>
                    </>
                  ) : mapError ? (
                    <>
                      <img src={AddressImg} alt={t("addAddress.map.placeholderAlt")} />
                      <div
                        style={{
                          padding: "15px",
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: "4px",
                          marginTop: "10px",
                          fontSize: "14px",
                          color: "#856404",
                        }}
                      >
                        <strong>⚠️ {t("addAddress.map.loadError.title")}</strong>
                        <p style={{ margin: "5px 0 0 0" }}>
                          {t("addAddress.map.loadError.message")}
                        </p>
                        <p
                          style={{
                            margin: "10px 0 0 0",
                            fontSize: "12px",
                            fontStyle: "italic",
                          }}
                        >
                          {t("addAddress.map.loadError.manualEntry")}
                        </p>
                      </div>
                    </>
                  ) : !isLoaded ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "400px",
                        backgroundColor: "#f5f5f5",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <p>{t("addAddress.map.loading")}</p>
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          border: "4px solid #f3f3f3",
                          borderTop: "4px solid #3498db",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      <style>{`
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}</style>
                    </div>
                  ) : (
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "400px",
                      }}
                    >
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={mapCenter}
                        zoom={markerPosition ? 15 : 12}
                        onLoad={onMapLoad}
                        onClick={onMapClick}
                        options={{
                          disableDefaultUI: false,
                          zoomControl: true,
                          streetViewControl: false,
                          mapTypeControl: false,
                        }}
                      >
                        {markerPosition && <Marker position={markerPosition} />}
                      </GoogleMap>
                    </div>
                  )}
                  <div
                    className={styles["searchBar"]}
                    style={{ position: "relative", width: "100%" }}
                    ref={dropdownRef}
                  >
                    {isLoaded && apiKey && !mapError ? (
                      <>
                        <div style={{ position: "relative" }}>
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t("addAddress.search.placeholder")}
                            value={selectedAddressText}
                            onChange={(e) =>
                              handleLocationInputChange(e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                            autoComplete="off"
                            style={{
                              width: "100%",
                              padding: "10px 40px 10px 15px",
                              border:
                                locationSuggestions.length > 0
                                  ? "1px solid #0d6efd"
                                  : "1px solid #ddd",
                              borderRadius:
                                locationSuggestions.length > 0
                                  ? "8px 8px 0 0"
                                  : "8px",
                              fontSize: "14px",
                              outline: "none",
                              transition: "all 0.2s ease",
                            }}
                          />
                          <span
                            className={styles["locationIcon"]}
                            onClick={handleGetCurrentLocation}
                            style={{
                              cursor: "pointer",
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 10,
                            }}
                            title={t("addAddress.search.getCurrentLocation")}
                          >
                            <TbCurrentLocation />
                          </span>
                        </div>
                        {locationSuggestions.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #dee2e6",
                              borderTop: "none",
                              borderRadius: "0 0 8px 8px",
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                              top: "100%",
                            }}
                          >
                            {locationSuggestions.map((suggestion, idx) => (
                              <div
                                key={suggestion.placeId}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom:
                                    idx === locationSuggestions.length - 1
                                      ? "none"
                                      : "1px solid #f0f0f0",
                                  background: "#fff",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  handleLocationInputChange(
                                    suggestion.description,
                                  );
                                  const coords =
                                    await fetchCoordinatesFromPlaceId(
                                      suggestion.placeId,
                                    );
                                  if (coords) {
                                    setLatitude(coords.latitude);
                                    setLongitude(coords.longitude);
                                    setMapCenter({
                                      lat: coords.latitude,
                                      lng: coords.longitude,
                                    });
                                    setMarkerPosition({
                                      lat: coords.latitude,
                                      lng: coords.longitude,
                                    });
                                    if (coords.formattedAddress) {
                                      setSelectedAddressText(
                                        coords.formattedAddress,
                                      );
                                      if (searchInputRef.current) {
                                        searchInputRef.current.value =
                                          coords.formattedAddress;
                                      }
                                    }
                                    if (mapRef.current) {
                                      mapRef.current.setCenter({
                                        lat: coords.latitude,
                                        lng: coords.longitude,
                                      });
                                      mapRef.current.setZoom(15);
                                    }
                                  }
                                  setLocationSuggestions([]);
                                  sessionTokenRef.current = null;
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f8f9fa";
                                  e.currentTarget.style.paddingLeft = "20px";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#fff";
                                  e.currentTarget.style.paddingLeft = "16px";
                                }}
                              >
                                <TbLocationFilled
                                  size={18}
                                  style={{
                                    color: "#0d6efd",
                                    marginTop: "2px",
                                    flexShrink: 0,
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      color: "#212529",
                                      lineHeight: "1.5",
                                      fontWeight: "400",
                                    }}
                                  >
                                    {suggestion.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder={t("addAddress.search.placeholderApiKeyRequired")}
                          disabled
                          style={{
                            width: "100%",
                            padding: "10px 40px 10px 15px",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "#f5f5f5",
                          }}
                        />
                        <span
                          className={styles["locationIcon"]}
                          onClick={handleGetCurrentLocation}
                          style={{
                            cursor: "pointer",
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 10,
                          }}
                          title={t("addAddress.search.getCurrentLocation")}
                        >
                          <TbCurrentLocation />
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {selectedAddressText && (
                  <div className={styles["houseLocation"]}>
                    <span className={styles["locationIcon"]}>
                      <TbLocationFilled />
                    </span>
                    <div className={styles["locationDetail"]}>
                      {formData.house_flat_floor_no && (
                        <p className={styles["houseNum"]}>
                          {formData.house_flat_floor_no}
                        </p>
                      )}
                      <p>{selectedAddressText}</p>
                    </div>
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className={styles["location-form"]}
                >
                  {/* Name Field */}
                  <div className={styles["form-group"]}>
                    <label htmlFor="name">{t("addAddress.form.name")}</label>
                    <input
                      type="text"
                      id="name"
                      placeholder={t("addAddress.form.namePlaceholder")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`${styles["form-input"]} ${
                        errors.name ? styles["input-error"] : ""
                      }`}
                    />
                    {errors.name && (
                      <p className={styles["error-text"]}>{errors.name}</p>
                    )}
                  </div>

                  {/* Address Details */}
                  <div className={styles["address-details-grid"]}>
                    <div className={styles["form-group"]}>
                      <label htmlFor="house_flat_floor_no">
                        {t("registration.houseFlatFloor")}
                      </label>
                      <input
                        type="text"
                        id="house_flat_floor_no"
                        placeholder={t("registration.placeholder.houseFlatFloor")}
                        value={formData.house_flat_floor_no}
                        onChange={(e) =>
                          handleInputChange(
                            "house_flat_floor_no",
                            e.target.value,
                          )
                        }
                        className={`${styles["form-input"]} ${
                          errors.house_flat_floor_no
                            ? styles["input-error"]
                            : ""
                        }`}
                      />
                      {errors.house_flat_floor_no && (
                        <p className={styles["error-text"]}>
                          {errors.house_flat_floor_no}
                        </p>
                      )}
                    </div>

                    <div className={styles["form-group"]}>
                      <label htmlFor="street">{t("addAddress.form.street")}</label>
                      <input
                        type="text"
                        id="street"
                        placeholder={t("registration.placeholder.street")}
                        value={formData.street}
                        onChange={(e) =>
                          handleInputChange("street", e.target.value)
                        }
                        className={`${styles["form-input"]} ${
                          errors.street ? styles["input-error"] : ""
                        }`}
                      />
                      {errors.street && (
                        <p className={styles["error-text"]}>{errors.street}</p>
                      )}
                    </div>

                    <div className={styles["form-group"]}>
                      <label htmlFor="area_district">{t("addAddress.form.areaDistrict")}</label>
                      <input
                        type="text"
                        id="area_district"
                        placeholder={t("registration.placeholder.areaDistrict")}
                        value={formData.area_district}
                        onChange={(e) =>
                          handleInputChange("area_district", e.target.value)
                        }
                        className={styles["form-input"]}
                      />
                    </div>

                    <div className={styles["form-group"]}>
                      <label htmlFor="city">{t("addAddress.form.city")}</label>
                      <input
                        type="text"
                        id="city"
                        placeholder={t("registration.placeholder.city")}
                        value={formData.city}
                        onChange={(e) =>
                          handleInputChange("city", e.target.value)
                        }
                        className={styles["form-input"]}
                      />
                    </div>

                    <div className={styles["form-group"]}>
                      <label htmlFor="governorate">{t("addAddress.form.governorate")}</label>
                      <input
                        type="text"
                        id="governorate"
                        placeholder={t("registration.placeholder.governorate")}
                        value={formData.governorate}
                        onChange={(e) =>
                          handleInputChange("governorate", e.target.value)
                        }
                        className={styles["form-input"]}
                      />
                    </div>

                    <div className={styles["form-group"]}>
                      <label htmlFor="country">{t("registration.country")}</label>
                      <input
                        type="text"
                        id="country"
                        placeholder={t("registration.placeholder.country")}
                        value={formData.country}
                        onChange={(e) =>
                          handleInputChange("country", e.target.value)
                        }
                        className={styles["form-input"]}
                      />
                    </div>

                    <div className={styles["form-group"]}>
                      <label htmlFor="postalCode">{t("registration.postalCode")}</label>
                      <input
                        type="text"
                        id="postalCode"
                        placeholder={t("registration.placeholder.postalCode")}
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        className={styles["form-input"]}
                      />
                    </div>
                  </div>
                </form>

                {/* Confirm and Continue Button */}
                <button
                  onClick={handleConfirmAndContinue}
                  className={styles["login-btn"]}
                  disabled={loading}
                >
                  {loading
                    ? t("common.saving")
                    : editingAddressId
                      ? t("addAddress.button.updateAddress")
                      : t("addAddress.button.confirmAndContinue")}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* all addresses */}
              <div className={styles["all-addresses"]}>
                <div className={styles["topBar"]}>
                  <h2 className={styles["addressTitle"]}>{t("addAddress.list.title")}</h2>
                  <button
                    className={styles["login-btn"]}
                    onClick={() => setAddAddress(true)}
                  >
                    <IoAddCircleOutline /> {t("common.add")}
                  </button>
                </div>

                <div className="addressList">
                  {loading && addresses.length === 0 ? (
                    <p>{t("addAddress.list.loading")}</p>
                  ) : addresses.length === 0 ? (
                    <p style={{ textAlign: "center", padding: "20px" }}>
                      {t("addAddress.list.empty")}
                    </p>
                  ) : (
                    addresses.map((address) => (
                      <AddressCard
                        key={address.id}
                        address={address}
                        isDefault={address.is_default}
                        onSetDefault={() => handleSetDefaultAddress(address.id)}
                        onDelete={() => handleDeleteAddress(address.id)}
                        onEdit={() => handleEditAddress(address)}
                      />
                    ))
                  )}
                </div>

                {addresses.length > 0 && (
                  <div className={styles["ftr-btn-wpr"]}>
                    <button
                      className={styles["login-btn"]}
                      onClick={() => {
                        // Navigate or perform final action
                        toast.success(t("addAddress.toast.addressesSaved"));
                      }}
                    >
                      {t("addAddress.button.saveAndContinue")}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AddAddress;
